import { useReactiveVar } from "@apollo/client";
import { Href, usePathname, useRouter } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Platform } from "react-native";

import PlayerHeaderView from "./HeaderPlayerView";

import { PlayerDetailPlayerMetaFragment, Watchlist } from "@/generated/graphql";
import useExpandedPlayerUrl from "@/hooks/useExpandedPlayerUrl";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useDeleteRecommendationPlayer } from "@/hooks/usePlayer";
import { playerDetailVar } from "@/stores/player";
import {
  flagUrlForNationality,
  resolveNationality,
} from "@/utils/nationalityRegistry";

/**
 * Props for the HeaderPlayerContainer component
 */
interface Props {
  player: PlayerDetailPlayerMetaFragment;
  isGoBack?: boolean;
  isSimilarDetail?: boolean;
  activeWatchList?: Watchlist;
  onAddPlayer?: () => void;
}

/**
 * Container component for the player header with player information and actions.
 *
 * Manages player header state, navigation actions, and platform-specific confirmation dialogs.
 * Uses Platform checks to provide appropriate delete confirmation UI:
 * - Web: window.confirm dialog
 * - iOS/Android: Alert.alert with Cancel and Yes buttons
 *
 * Navigation uses nested player routes structure:
 * - Similar players: /players/{id}/similar
 * - Compare players: /players/{id}/compare with watchlistId and watchlistName params
 * @param root0 - Component props
 * @param root0.player - Player data including bio, positions, team, and nationality information
 * @param root0.isGoBack - Whether to show back navigation instead of compare action
 * @param root0.isSimilarDetail - Whether this header is displayed in similar player detail view
 * @param root0.activeWatchList - Currently active watchlist for player comparison navigation
 * @param root0.onAddPlayer - Callback invoked when user adds player to a list
 * @remarks
 * - Delete confirmation uses Platform.OS check to provide native experience on each platform
 * - Follows cross-platform pattern from PlayerItemContainer (see components/PlayerItem/PlayerItemContainer.tsx:125-148)
 * - Navigation paths migrated from top-level routes (/similar-players, /compare-players) to nested routes under /players/{id}
 */
const PlayerHeaderContainer = ({
  player,
  isGoBack,
  isSimilarDetail,
  activeWatchList,
  onAddPlayer,
}: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const infoTemp = useReactiveVar(playerDetailVar);
  const isSmallScreen = useMobileScreen();
  const { deleteRecommendationPlayer } = useDeleteRecommendationPlayer();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const timeOutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const _isOnPlayerRoute = pathname?.includes("/players/");
  const bioData = player?.bioData;
  const foot =
    bioData?.foot === "both"
      ? "Both"
      : (bioData?.foot?.[0]?.toUpperCase?.() ?? "");
  const positionAbbrev = player.positions
    ?.filter(p => p?.abbreviation)
    .map(p => p?.abbreviation)
    .join(", ");
  const knownName = player?.name ?? "";
  const age = bioData?.age?.toString();
  const birthYear = bioData?.dateOfBirth?.split("-")[0]?.slice(2);
  const ageAndBirthYear = age && birthYear ? `${age} ('${birthYear})` : "";
  const playerAgent =
    bioData?.playerAgent !== "no agent" && bioData?.playerAgent
      ? bioData?.playerAgent
      : "";
  const tidbits = [
    positionAbbrev ?? "",
    player.team?.name ?? player.teamName ?? "",
    ageAndBirthYear,
    foot,
    playerAgent,
  ].filter(Boolean);
  const roleArchetype = bioData?.roleArchetype ?? "";
  const secondaryRoleArchetype = bioData?.secondaryRoleArchetype ?? null;
  const valuationStatus = bioData?.valuationStatus;
  const onLoanFrom = bioData?.onLoanFrom;
  const isInjured = bioData?.isInjured;
  // Shares the player-profile header's resolver, so a nationality that shows a
  // flag on one surface shows one here too (SE-6241). Platform selects the
  // asset format only: PNG for native, which cannot render SVG.
  const nationalityEntry = resolveNationality(player.nationality?.name);
  const nationalityFlagUrl = nationalityEntry
    ? flagUrlForNationality(nationalityEntry, Platform.OS)
    : undefined;

  const handleClosePlayer = useCallback(() => {
    router.back();
  }, [router]);

  const handleDeleteRecommendationPlayer = useCallback(() => {
    const playerName = player?.name ?? "";

    if (Platform.OS === "web") {
      const doDelete = window.confirm(
        `Are you sure you want to remove ${playerName} from the recommendation list?`
      );
      return doDelete ? deleteRecommendationPlayer() : null;
    }

    return new Promise(resolve =>
      Alert.alert(
        `Remove ${playerName}`,
        `Are you sure you want to remove ${playerName} from the recommendation list?`,
        [
          { text: "Cancel", style: "cancel", onPress: () => resolve(null) },
          {
            text: "Yes",
            onPress: () => {
              deleteRecommendationPlayer();
              resolve(null);
            },
            style: "destructive",
          },
        ]
      )
    );
  }, [deleteRecommendationPlayer, player]);

  const similarLink = useExpandedPlayerUrl(`/players/${player.id}/similar`);
  const handleSimilarPlayer = useCallback(() => {
    router.push(similarLink as Href);
  }, [router, similarLink]);

  const compareLink = useExpandedPlayerUrl(`/players/${player.id}/compare`);
  const handleComparePlayer = useCallback(() => {
    router.push({
      pathname: compareLink,
      params: {
        watchlistId: activeWatchList?.id,
        watchlistName: activeWatchList?.name ?? "",
      },
    } as Href);
  }, [router, compareLink, activeWatchList?.id, activeWatchList?.name]);

  const handleAddReport = useCallback(() => {
    if (!player?.id) {
      console.error("Cannot navigate to add report: player ID is missing");
      return;
    }
    router.push(`./${player.id}/matches`);
  }, [router, player.id]);

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleAddToList = useCallback(() => {
    setIsMenuOpen(false);
    onAddPlayer?.();
  }, [onAddPlayer]);

  const handleAddReportFromMenu = useCallback(() => {
    setIsMenuOpen(false);
    timeOutId.current = setTimeout(() => {
      handleAddReport();
    }, 500);
  }, [handleAddReport]);

  const getValuationAction = useCallback(
    (status: string | null | undefined): "success" | undefined => {
      if (!status) return undefined;
      switch (status) {
        case "Undervalued":
        case "Severely Undervalued":
          return "success";
        default:
          return undefined;
      }
    },
    []
  );

  const funcDelete = useMemo(() => {
    if (infoTemp?.type === "recommendation") {
      return {
        onDelete: handleDeleteRecommendationPlayer,
      };
    }
    return {
      onCompare: isGoBack ? undefined : handleComparePlayer,
    };
  }, [
    handleComparePlayer,
    handleDeleteRecommendationPlayer,
    infoTemp?.type,
    isGoBack,
  ]);

  useEffect(() => {
    return () => {
      if (timeOutId.current) {
        clearTimeout(timeOutId.current);
      }
    };
  }, []);

  return (
    <PlayerHeaderView
      roleArchetype={roleArchetype}
      secondaryRoleArchetype={secondaryRoleArchetype}
      valuationStatus={valuationStatus}
      onLoanFrom={onLoanFrom}
      limitedDataWarning={bioData?.limitedDataWarning}
      knownName={knownName}
      isSimilarDetail={isSimilarDetail}
      tidbits={tidbits ?? []}
      isInjured={isInjured}
      nationalityFlagUrl={nationalityFlagUrl}
      isSmallScreen={isSmallScreen}
      getValuationAction={getValuationAction}
      onAdd={onAddPlayer}
      onAddReport={handleAddReport}
      onClose={handleClosePlayer}
      onSimilar={handleSimilarPlayer}
      isMenuOpen={isMenuOpen}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      onAddToList={handleAddToList}
      onAddReportFromMenu={handleAddReportFromMenu}
      {...funcDelete}
    />
  );
};

export default React.memo(PlayerHeaderContainer);
